import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from './cn.ts';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  className,
  size = 'md',
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Focus trap: focus dialog on open
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={ref}
        tabIndex={-1}
        className={cn(
          'relative w-full bg-white rounded-2xl shadow-xl border border-zinc-200 outline-none',
          'flex flex-col max-h-[90vh]',
          sizes[size],
          className,
        )}
      >
        {/* Header — fisso */}
        {(title || description) && (
          <div className="px-6 pt-5 pb-4 shrink-0 border-b border-zinc-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                {title && (
                  <h2 id="modal-title" className="text-base font-semibold text-zinc-900">
                    {title}
                  </h2>
                )}
                {description && <p className="mt-0.5 text-sm text-zinc-500">{description}</p>}
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-md transition-colors -mt-0.5"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Body — scrollabile */}
        <div className="px-6 py-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 pt-4 border-t border-zinc-100 mt-2',
        className,
      )}
    >
      {children}
    </div>
  );
}
